import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { checkImageLibraryRequiredActions } from 'app/plugins/openstack/components/images/checkImageLibraryRequiredActions'
import { listResmgrHosts } from 'app/plugins/openstack/components/infrastructure/actions'
import { resmgrHostsSelector } from 'app/plugins/openstack/components/infrastructure/selectors'
import { useAppSelector } from 'app/store'
import ActionRequiredModalTrigger, {
  ActionRequiredIssue,
} from 'core/components/ActionRequiredModalTrigger'
import ListContainer from 'core/containers/ListContainer'
import Badge from 'core/elements/badge'
import { createGridStatusCell } from 'core/elements/grid/cells/GridStatusCell'
import Tooltip from 'core/elements/tooltip'
import useListAction from 'core/hooks/useListAction'
import { useLocalNetworkAccessAllowed } from 'core/hooks/useLocalNetworkAccessAllowed'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { getProductName } from 'core/utils/helpers'
import { pick } from 'ramda'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import {
  listComputeServiceDetails,
  listImageServiceDetails,
  listNetworkServiceDetails,
  listStorageServiceDetails,
} from './action'
import { ServiceType } from './constant'
import { HealthStatusBadge } from './HealthStatusBadge'
import {
  buildCompleteCombinedServices,
  getConnectivityStatus,
  IPAddressCellComponent,
  validServiceDetails,
} from './helper'
import { combinedServicesSelector, ServiceStatus } from './selector'

const hostColumns = ({
  isBrowserLocalNetworkAccessAllowed,
}: {
  isBrowserLocalNetworkAccessAllowed?: boolean
}) => [
  {
    key: 'name',
    label: 'Host Name',
  },
  {
    key: 'clusterName',
    label: 'Cluster',
  },
  {
    key: 'connectivityStatus',
    label: (
      <Tooltip
        message={`Reports the status of the connection between ${getProductName()} management plane and the host`}
      >
        <span>Connectivity Status</span>
      </Tooltip>
    ),
    CellComponent: createGridStatusCell({
      dataFn: (phase: string) => getConnectivityStatus(phase),
    }),
  },
  {
    key: 'computeStatus',
    label: 'Compute Service',
    CellComponent: ({ value, item }) => (
      <HealthStatusBadge value={value} serviceType={ServiceType.Compute} />
    ),
  },
  {
    key: 'imageStatus',
    label: 'Image Service',
    CellComponent: ({ value, item }) => {
      const issues: ActionRequiredIssue[] = useMemo(() => {
        const needToValidateCert = value === ServiceStatus.CertificateError
        const glanceHostNotReachable = value === ServiceStatus.NetworkError

        return checkImageLibraryRequiredActions({
          needToValidateCert,
          glanceHostNotReachable,
          isBrowserLocalNetworkAccessAllowed,
          hostEndpoint: item?.endpoint || item?.host,
        })
      }, [isBrowserLocalNetworkAccessAllowed, item?.endpoint, item?.host, value])

      return issues.length > 0 && value !== 'N/A' ? (
        <ActionRequiredModalTrigger
          issues={issues}
          modalTitle="Needs Action"
          modalIntroText="The following actions are required to access the Image Library Endpoint:"
          trigger={({ openWithEvent }) => (
            <Badge
              variant="warning"
              text={`Needs Action (${issues.length})`}
              ellipsisAt={50}
              onClick={openWithEvent}
            />
          )}
        />
      ) : (
        <HealthStatusBadge
          value={value}
          serviceType={ServiceType.Image}
          endpoint={item?.endpoint || item?.host}
        />
      )
    },
  },
  {
    key: 'storageStatus',
    label: 'Persistent Storage Service',
    CellComponent: ({ value }) => (
      <HealthStatusBadge value={value} serviceType={ServiceType.Storage} />
    ),
  },
  {
    key: 'networkStatus',
    label: 'Networking Service',
    CellComponent: ({ value }) => (
      <HealthStatusBadge value={value} serviceType={ServiceType.Network} />
    ),
  },
  {
    key: 'ipAddresses',
    label: 'IP Addresses',
    CellComponent: IPAddressCellComponent,
  },
]
type Params = {}
const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ResmgrHosts',
  listTablePrefs,
)

export default function ServiceHealthHostsListPage() {
  const { params, getParamsUpdater } = usePrefParams({})

  const { loading: loadingHosts, reload: reloadHosts } = useListAction(listResmgrHosts)
  const { loading: loadingComputeService, reload: reloadComputeService } = useListAction(
    listComputeServiceDetails,
  )
  const { loading: loadingStorageService, reload: reloadStorageService } = useListAction(
    listStorageServiceDetails,
  )
  const { loading: loadingNetworkService, reload: reloadNetworkService } = useListAction(
    listNetworkServiceDetails,
  )
  const { loading: imageLoading, reload: reloadImageService } = useListAction(
    listImageServiceDetails,
  )
  const combinedServicesDetails = useAppSelector(combinedServicesSelector)
  const hosts = useSelector(resmgrHostsSelector)

  // Filter out services that do not have host details
  const validCombinedServicesDetails = useMemo(() => {
    return validServiceDetails(combinedServicesDetails)
  }, [combinedServicesDetails])

  const completeCombinedServices = useMemo(
    () => buildCompleteCombinedServices(validCombinedServicesDetails, hosts),
    [validCombinedServicesDetails, hosts],
  )

  const handleRefresh = useCallback(() => {
    reloadComputeService(true)
    reloadStorageService(true)
    reloadNetworkService(true)
    reloadImageService(true)
    reloadHosts(true)
  }, [reloadHosts, reloadComputeService, reloadStorageService, reloadNetworkService])

  const isBrowserLocalNetworkAccessAllowed = useLocalNetworkAccessAllowed()

  return (
    <>
      <ListContainer<any, any>
        uniqueIdentifier={'name'}
        label="Host Health"
        searchTargets={['name', 'clusterName']}
        loading={
          loadingHosts ||
          loadingComputeService ||
          loadingStorageService ||
          loadingNetworkService ||
          imageLoading
        }
        loadingMessage={'Loading...'}
        onRefresh={handleRefresh}
        data={completeCombinedServices}
        columns={hostColumns({ isBrowserLocalNetworkAccessAllowed })}
        emptyContent="No hosts found"
        hideCheckboxes
        showBreadcrumbs={false}
        {...pick(listTablePrefs, params)}
        getParamsUpdater={getParamsUpdater}
      />
    </>
  )
}
